import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/shared/ui/card';
import {
  ChevronDown,
  ChevronRight,
  Download,
  Edit,
  Eye,
  FileText,
  Plus,
  Trash2,
  BadgePercent,
  Loader2,
} from 'lucide-react';
import { ReceivableStatusBadge, ReceivableFilters, ReceivableDeleteDialog } from '../components';
import {
  useReceivables,
  useDeleteReceivable,
  useReceivableDocuments,
  useReceivableTaxes,
  useReceivableTaxDocuments,
} from '../hooks';
import {
  GetReceivablesParams,
  Receivable,
  ReceivableStatus,
  ReceivableDocument,
  ReceivableTax,
  ReceivableTaxDocument,
} from '../utils/types';
import { RECEIVABLE_DOCUMENT_TYPE_LABELS, ReceivableDocumentType } from '../utils/types/receivable-document.types';
import { RECEIVABLE_TAX_TYPE_LABELS, ReceivableTaxType, RECEIVABLE_TAX_DOCUMENT_TYPE_LABELS, ReceivableTaxDocumentType } from '../utils/types/receivable-tax.types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { Pagination } from '@/shared/widgets';
import { DateRange } from 'react-day-picker';
import { useTranslation } from '@/shared/i18n';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';

const MONTHS_PT = [
  '', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

function formatCurrency(currency: string, value: number) {
  const prefix = currency === 'BRL' ? 'R$' : currency === 'USD' ? 'US$' : currency;
  return `${prefix} ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
}

// Compatibilidade: aceita novos campos (month/year) ou legado (relatedDate)
function formatReferenceDate(month?: number, year?: number, relatedDate?: string) {
  if (month && year) {
    return `${MONTHS_PT[month]}/${year}`;
  }
  // Fallback: parsear de relatedDate legado (YYYY-MM-DD ou ISO)
  if (relatedDate) {
    const datePart = relatedDate.includes('T') ? relatedDate.split('T')[0] : relatedDate;
    const [y, m] = datePart.split('-').map(Number);
    if (y && m) return `${MONTHS_PT[m]}/${y}`;
  }
  return '-';
}

function formatPaidDate(dateString?: string) {
  if (!dateString) return null;
  let datePart: string;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    datePart = dateString;
  } else if (/^\d{4}-\d{2}-\d{2}T/.test(dateString)) {
    datePart = dateString.split('T')[0];
  } else {
    return dateString;
  }
  const [year, month, day] = datePart.split('-').map(Number);
  return format(new Date(year, month - 1, day), 'dd/MM/yyyy', { locale: ptBR });
}

// --- Expandable row detail: Documents tab ---
function ReceivableDocumentsTab({ receivableId }: { receivableId: string }) {
  const { documents, isLoading } = useReceivableDocuments(receivableId);
  const [previewDoc, setPreviewDoc] = useState<ReceivableDocument | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando documentos...
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
        Nenhum documento anexado.
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="flex items-center justify-between rounded-md border bg-muted/20 px-4 py-3"
          >
            <div className="flex items-center gap-3">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <div>
                <Badge variant="secondary" className="text-xs">
                  {RECEIVABLE_DOCUMENT_TYPE_LABELS[doc.documentType as ReceivableDocumentType] ?? doc.documentType}
                </Badge>
                {doc.description && (
                  <p className="mt-1 text-xs text-muted-foreground">{doc.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {doc.documentUrl && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setPreviewDoc(doc)}
                    title="Visualizar"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button asChild variant="ghost" size="icon" title="Baixar">
                    <a href={doc.documentUrl} target="_blank" rel="noopener noreferrer">
                      <Download className="h-4 w-4" />
                    </a>
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal de visualização de documento */}
      <Dialog open={!!previewDoc} onOpenChange={(open) => !open && setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {previewDoc && (RECEIVABLE_DOCUMENT_TYPE_LABELS[previewDoc.documentType as ReceivableDocumentType] ?? 'Documento')}
            </DialogTitle>
          </DialogHeader>
          {previewDoc?.documentUrl && (
            <div className="flex flex-col gap-3">
              {previewDoc.description && (
                <p className="text-sm text-muted-foreground">{previewDoc.description}</p>
              )}
              <iframe
                src={previewDoc.documentUrl}
                className="w-full h-[70vh] rounded-md border"
                title="Visualização do documento"
              />
              <div className="flex justify-end">
                <Button asChild variant="outline" size="sm">
                  <a href={previewDoc.documentUrl} target="_blank" rel="noopener noreferrer">
                    <Download className="mr-2 h-4 w-4" /> Abrir em nova aba
                  </a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// --- Expandable tax documents ---
function TaxDocumentsList({ taxId }: { taxId: string }) {
  const { documents, isLoading } = useReceivableTaxDocuments(taxId);
  const [previewDoc, setPreviewDoc] = useState<ReceivableTaxDocument | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Carregando...
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <p className="py-2 text-xs text-muted-foreground">Nenhum documento nesta taxa.</p>
    );
  }

  return (
    <>
      <div className="space-y-1 pl-4">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="flex items-center justify-between rounded-md border bg-muted/10 px-3 py-2"
          >
            <div className="flex items-center gap-2">
              <FileText className="h-3 w-3 text-muted-foreground" />
              <Badge variant="outline" className="text-xs">
                {RECEIVABLE_TAX_DOCUMENT_TYPE_LABELS[doc.documentType as ReceivableTaxDocumentType] ?? doc.documentType}
              </Badge>
              {doc.description && (
                <span className="text-xs text-muted-foreground">{doc.description}</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {doc.documentUrl && (
                <>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPreviewDoc(doc)} title="Visualizar">
                    <Eye className="h-3 w-3" />
                  </Button>
                  <Button asChild variant="ghost" size="icon" className="h-7 w-7" title="Baixar">
                    <a href={doc.documentUrl} target="_blank" rel="noopener noreferrer">
                      <Download className="h-3 w-3" />
                    </a>
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!previewDoc} onOpenChange={(open) => !open && setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {previewDoc && (RECEIVABLE_TAX_DOCUMENT_TYPE_LABELS[previewDoc.documentType as ReceivableTaxDocumentType] ?? 'Documento da taxa')}
            </DialogTitle>
          </DialogHeader>
          {previewDoc?.documentUrl && (
            <div className="flex flex-col gap-3">
              {previewDoc.description && (
                <p className="text-sm text-muted-foreground">{previewDoc.description}</p>
              )}
              <iframe
                src={previewDoc.documentUrl}
                className="w-full h-[70vh] rounded-md border"
                title="Visualização do documento"
              />
              <div className="flex justify-end">
                <Button asChild variant="outline" size="sm">
                  <a href={previewDoc.documentUrl} target="_blank" rel="noopener noreferrer">
                    <Download className="mr-2 h-4 w-4" /> Abrir em nova aba
                  </a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// --- Expandable row detail: Taxes tab ---
function ReceivableTaxesTab({ receivableId }: { receivableId: string }) {
  const { taxes, isLoading } = useReceivableTaxes(receivableId);
  const [expandedTaxId, setExpandedTaxId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando taxas...
      </div>
    );
  }

  if (!taxes || taxes.length === 0) {
    return (
      <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
        Nenhuma taxa cadastrada.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {taxes.map((tax) => {
        const isExpanded = expandedTaxId === tax.id;
        return (
          <Collapsible key={tax.id} open={isExpanded} onOpenChange={(open) => setExpandedTaxId(open ? tax.id : null)}>
            <div className="rounded-md border bg-muted/20">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <BadgePercent className="h-4 w-4 text-muted-foreground" />
                    <Badge variant="secondary" className="text-xs">
                      {RECEIVABLE_TAX_TYPE_LABELS[tax.type as ReceivableTaxType] ?? tax.type}
                    </Badge>
                    <span className="font-mono text-sm">
                      {formatCurrency(tax.currency, tax.value)}
                    </span>
                    {tax.description && (
                      <span className="text-xs text-muted-foreground hidden md:inline">
                        {tax.description}
                      </span>
                    )}
                  </div>
                  {isExpanded
                    ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  }
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="border-t px-4 py-3">
                  <TaxDocumentsList taxId={tax.id} />
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        );
      })}
    </div>
  );
}

// --- Expandable row detail ---
function ReceivableExpandedRow({ receivable }: { receivable: Receivable }) {
  return (
    <div className="px-4 pb-4 pt-2">
      <Tabs defaultValue="documents" className="w-full">
        <TabsList>
          <TabsTrigger value="documents">Documentos</TabsTrigger>
          <TabsTrigger value="taxes">Taxas</TabsTrigger>
        </TabsList>
        <TabsContent value="documents">
          <ReceivableDocumentsTab receivableId={receivable.id} />
        </TabsContent>
        <TabsContent value="taxes">
          <ReceivableTaxesTab receivableId={receivable.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export const ReceivablesList = () => {
  const { t } = useTranslation('receivables');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const [search, setSearch] = useState(searchParams.get('search') ?? '');
  const [statusFilter, setStatusFilter] = useState<ReceivableStatus | 'all'>(
    (searchParams.get('status') as ReceivableStatus | 'all') ?? 'all'
  );

  const [paidDateRange, setPaidDateRange] = useState<DateRange | undefined>(() => {
    const from = searchParams.get('paidDateStart');
    const to = searchParams.get('paidDateEnd');
    if (!from && !to) return undefined;
    return {
      from: from ? new Date(from) : undefined,
      to: to ? new Date(to) : undefined,
    };
  });

  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page') ?? '1') || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('pageSize') ?? '10') || 10);
  const debouncedSearch = useDebounce(search, 400);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [receivableToDelete, setReceivableToDelete] = useState<Receivable | null>(null);

  const formatDateStartForApi = useCallback((date?: Date): string | undefined => {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00.000Z`;
  }, []);

  const formatDateEndForApi = useCallback((date?: Date): string | undefined => {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T23:59:59.999Z`;
  }, []);

  const filtrosApi: GetReceivablesParams = {
    page: currentPage,
    limit: pageSize,
    status: statusFilter,
    search: debouncedSearch,
    sortBy: 'createdAt',
    order: 'desc',
    paidDateStart: formatDateStartForApi(paidDateRange?.from),
    paidDateEnd: formatDateEndForApi(paidDateRange?.to),
  };

  const {
    receivables,
    totalPages,
    totalItems,
    currentPage: currentPageFromApi,
    isLoading,
    error,
    queryResult,
  } = useReceivables(filtrosApi);

  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (paidDateRange?.from) params.set('paidDateStart', formatDateStartForApi(paidDateRange.from)!);
    if (paidDateRange?.to) params.set('paidDateEnd', formatDateEndForApi(paidDateRange.to)!);
    if (currentPage > 1) params.set('page', String(currentPage));
    if (pageSize !== 10) params.set('pageSize', String(pageSize));
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, statusFilter, paidDateRange, currentPage, pageSize, setSearchParams, formatDateStartForApi, formatDateEndForApi]);

  useEffect(() => { setCurrentPage(1); }, [debouncedSearch, statusFilter, paidDateRange, pageSize]);
  useEffect(() => { if (currentPageFromApi && currentPageFromApi !== currentPage) setCurrentPage(currentPageFromApi); }, [currentPageFromApi, currentPage]);
  useEffect(() => { const apiLimit = queryResult.data?.limit; if (typeof apiLimit === 'number' && apiLimit > 0 && apiLimit !== pageSize) setPageSize(apiLimit); }, [pageSize, queryResult.data?.limit]);

  const { deleteReceivable, isLoading: isDeleting } = useDeleteReceivable();
  const handleEdit = (id: string) => navigate(`/receivables/${id}`);
  const handleDelete = (receivable: Receivable) => { setReceivableToDelete(receivable); setDeleteDialogOpen(true); };
  const confirmDelete = async () => {
    if (!receivableToDelete) return;
    try {
      await deleteReceivable(receivableToDelete.id);
      setDeleteDialogOpen(false);
      setReceivableToDelete(null);
    } catch (error) {
      console.error('Erro ao excluir recebível:', error);
    }
  };

  const toggleRow = (id: string) => {
    setExpandedRowId((prev) => (prev === id ? null : id));
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('receivables')}</h1>
            <p className="text-muted-foreground">{t('manageReceivables')}</p>
          </div>
          <Button onClick={() => navigate('/receivables/new')}>
            <Plus className="mr-2 h-4 w-4" /> {t('newReceivable')}
          </Button>
        </div>
        <Card className="bg-white/[0.03] border-white/[0.06]">
          <CardContent className="p-6">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('receivables')}</h1>
          <p className="text-muted-foreground">{t('manageReceivables')}</p>
        </div>
        <Button onClick={() => navigate('/receivables/new')}>
          <Plus className="mr-2 h-4 w-4" /> {t('newReceivable')}
        </Button>
      </div>

      {/* Filtros */}
      <Card className='bg-white/[0.03] border-white/[0.06]'>
        <CardHeader>
          <CardTitle>{t('filters')}</CardTitle>
          <CardDescription>{t('filterByStatusDate')}</CardDescription>
        </CardHeader>
        <CardContent>
          <ReceivableFilters
            search={search}
            onSearchChange={setSearch}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            paidDateRange={paidDateRange}
            onPaidDateRangeChange={setPaidDateRange}
          />
        </CardContent>
      </Card>

      {/* Lista collapsible */}
      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardContent className="p-4">
          {error ? (
            <div className="p-6 text-center text-red-400">
              <p>{t('errorLoadingReceivables')}: {error.message}</p>
              <Button variant="outline" onClick={() => window.location.reload()} className="mt-2">
                Tentar novamente
              </Button>
            </div>
          ) : receivables.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">{t('emptyTitle')}</h3>
              <p className="mb-4 text-sm text-muted-foreground">
                {search || statusFilter !== 'all' ? t('emptyDescriptionWithFilters') : t('emptyDescription')}
              </p>
              {!(search || statusFilter !== 'all') && (
                <Button onClick={() => navigate('/receivables/new')}>
                  <Plus className="mr-2 h-4 w-4" /> {t('emptyActionLabel')}
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>{t('currency')}</TableHead>
                  <TableHead>{t('store')}</TableHead>
                  <TableHead>{t('status')}</TableHead>
                  <TableHead>{t('grossValue')}</TableHead>
                  <TableHead>Valor Liq.</TableHead>
                  <TableHead>{t('referenceMonth')}</TableHead>
                  <TableHead>{t('paidDate')}</TableHead>
                  <TableHead className="text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {receivables.map((item) => {
                  const isExpanded = expandedRowId === item.id;
                  return (
                    <Collapsible key={item.id} open={isExpanded} onOpenChange={() => toggleRow(item.id)} asChild>
                      <>
                        <TableRow
                          className="cursor-pointer hover:bg-muted/30 transition-colors"
                          onClick={() => toggleRow(item.id)}
                        >
                          <TableCell>
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => e.stopPropagation()}>
                                {isExpanded
                                  ? <ChevronDown className="h-4 w-4" />
                                  : <ChevronRight className="h-4 w-4" />
                                }
                              </Button>
                            </CollapsibleTrigger>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">{item.currency}</span>
                          </TableCell>
                          <TableCell>{item.store}</TableCell>
                          <TableCell>
                            <ReceivableStatusBadge status={item.status} />
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-muted-foreground/10 text-muted-foreground font-mono" variant="outline">
                              {formatCurrency(item.currency, item.grossValue)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {item.netValue != null ? (
                              <Badge className="bg-green-500/10 text-green-400 font-mono" variant="outline">
                                {formatCurrency(item.currency, item.netValue)}
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {formatReferenceDate(item.relatedMonth, item.relatedYear, item.relatedDate)}
                          </TableCell>
                          <TableCell>
                            {item.paidDate ? formatPaidDate(item.paidDate) : <Badge variant="secondary">-</Badge>}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(item.id)} title={t('edit')}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(item)} title={t('delete')}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        <CollapsibleContent asChild>
                          <tr>
                            <td colSpan={9} className="p-0">
                              <ReceivableExpandedRow receivable={item} />
                            </td>
                          </tr>
                        </CollapsibleContent>
                      </>
                    </Collapsible>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!error && totalPages > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          showPageSizeOptions
          pageSizeOptions={[10, 20, 50]}
          onPageSizeChange={(size) => { setCurrentPage(1); setPageSize(size); }}
          showTotalItems
          syncWithQueryParams={false}
        />
      )}

      <ReceivableDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        receivableId={receivableToDelete?.id || ''}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};
